import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Progress,
  SimpleGrid,
  useBreakpointValue,
} from '@chakra-ui/react';
import { 
  FaUsers,
  FaMusic,
  FaWallet,
  FaHandHoldingUsd
} from 'react-icons/fa';

const StatCard = ({ icon, label, value, color, progress, subtitle }) => {
  const cardBg = 'secondary.100';
  
  return (
    <Box
      p={4}
      bg={cardBg}
      borderRadius="lg"

      w="100%"
    >
      <VStack spacing={3} align="stretch">
        <HStack justify="space-between">
          <Icon as={icon} color={`${color}.400`} boxSize={5} />
          {progress !== undefined && (
            <Text fontSize="xs" color="gray.500">
              {Math.round(progress)}%
            </Text>
          )}
        </HStack>
        
        <VStack align="start" spacing={1}>
          <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
            {label}
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="white">
            {value}
          </Text>
          {subtitle && (
            <Text fontSize="xs" color="gray.500">
              {subtitle}
            </Text>
          )}
        </VStack>
        
        {progress !== undefined && (
          <Progress 
            value={progress} 
            colorScheme={color}
            size="sm"
            borderRadius="full"
            bg="gray.700"
          />
        )}
      </VStack>
    </Box>
  );
};

const WalletStats = ({ walletSummary }) => {
  // Responsive values
  const gridColumns = useBreakpointValue({ base: 1, sm: 2 });
  
  // Theme colors
  const cardBg = 'secondary.900';
  
  if (!walletSummary) {
    return (
      <Box
        w="100%"
        p={6}
        bg={cardBg}
        borderRadius="xl"
        border="1px solid"
        borderColor="gray.600"
      >
        <Text color="gray.400" textAlign="center">Loading stats...</Text>
      </Box>
    );
  }

  const {
    available_balance = 0,
    pending_earnings = 0,
    total_earned = 0,
    total_spent = 0,
    total_deposited = 0,
    eligible_earnings_amount = 0,
    pending_earnings_count = 0,
    total_sale_earnings = 0,
    total_referral_earnings = 0,
    pending_sale_earnings = 0,
    pending_referral_earnings = 0
  } = walletSummary;

  // Calculate metrics
  const totalIncome = total_deposited + total_earned; // Total money that came in
  const netBalance = totalIncome - total_spent; // Real net balance (income - spent)
  const earningsRatio = totalIncome > 0 ? (total_earned / totalIncome) * 100 : 0; // % from earnings vs deposits
  
  // Earnings breakdown
  const totalEarningsFromSales = total_sale_earnings + pending_sale_earnings;
  const totalEarningsFromReferrals = total_referral_earnings + pending_referral_earnings;
  const totalEarningsCalculated = totalEarningsFromSales + totalEarningsFromReferrals;
  const salesRatio = totalEarningsCalculated > 0 ? (totalEarningsFromSales / totalEarningsCalculated) * 100 : 0;
  const referralRatio = totalEarningsCalculated > 0 ? (totalEarningsFromReferrals / totalEarningsCalculated) * 100 : 0;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}k`;
    }
    return formatCurrency(amount);
  };

  return (
    <Box w="100%">
      <VStack spacing={4} align="stretch">
        <Text fontSize="md" fontWeight="bold" color="white" mb={2}>
          Wallet Statistics
        </Text>
        
        <SimpleGrid columns={gridColumns} spacing={3}>
          <StatCard
            icon={FaWallet}
            label="Net Balance"
            value={formatCompactCurrency(netBalance)}
            color={netBalance >= 0 ? 'green' : 'red'}
            subtitle={netBalance >= 0 ? 'Positive account' : 'Spent more than received'}
          />
          
          <StatCard
            icon={FaMusic}
            label="Total Sales"
            value={formatCompactCurrency(totalEarningsFromSales)}
            color="blue"
            progress={salesRatio}
            subtitle={`${Math.round(salesRatio)}% of total earnings`}
          />
          
          <StatCard
            icon={FaUsers}
            label="Referral Earnings"
            value={formatCompactCurrency(totalEarningsFromReferrals)}
            color="purple"
            progress={referralRatio}
            subtitle={`${Math.round(referralRatio)}% of total earnings`}
          />
          
          <StatCard
            icon={FaHandHoldingUsd}
            label="Earnings vs Deposits"
            value={`${Math.round(earningsRatio)}%`}
            color={earningsRatio > 50 ? 'green' : earningsRatio > 25 ? 'yellow' : 'blue'}
            progress={earningsRatio}
            subtitle={earningsRatio > 50 ? 'Earning well!' : 'Growing your balance'}
          />
        </SimpleGrid>

        {/* Summary Stats */}
        <Box 
          p={4} 
          bg="gray.800" 
          borderRadius="md" 
          border="1px solid" 
          borderColor="gray.600"
        >
          <VStack spacing={3}>
            <Text fontSize="sm" fontWeight="bold" color="gray.300">
              Financial Overview
            </Text>
            
            <SimpleGrid columns={2} spacing={6} w="100%">
              <VStack spacing={2} align="stretch">
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
                  Money In
                </Text>
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">Deposits:</Text>
                  <Text fontSize="sm" fontWeight="bold" color="blue.400">
                    {formatCompactCurrency(total_deposited)}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">Earnings:</Text>
                  <Text fontSize="sm" fontWeight="bold" color="green.400">
                    {formatCompactCurrency(total_earned)}
                  </Text>
                </HStack>
                <HStack justify="space-between" borderTop="1px solid" borderColor="gray.600" pt={2}>
                  <Text fontSize="xs" color="gray.400">Total In:</Text>
                  <Text fontSize="sm" fontWeight="bold" color="white">
                    {formatCompactCurrency(totalIncome)}
                  </Text>
                </HStack>
              </VStack>
              
              <VStack spacing={2} align="stretch">
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
                  Current Status
                </Text>
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">Available:</Text>
                  <Text fontSize="sm" fontWeight="bold" color="green.400">
                    {formatCompactCurrency(available_balance)}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">Pending:</Text>
                  <Text fontSize="sm" fontWeight="bold" color="yellow.400">
                    {formatCompactCurrency(pending_earnings)}
                  </Text>
                </HStack>
                <HStack justify="space-between" borderTop="1px solid" borderColor="gray.600" pt={2}>
                  <Text fontSize="xs" color="gray.400">Net Worth:</Text>
                  <Text fontSize="sm" fontWeight="bold" color={netBalance >= 0 ? "green.400" : "red.400"}>
                    {formatCompactCurrency(netBalance)}
                  </Text>
                </HStack>
              </VStack>
            </SimpleGrid>
            
            {(eligible_earnings_amount > 0 || pending_earnings_count > 0) && (
              <Box w="100%" pt={2} borderTop="1px solid" borderColor="gray.600">
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">
                    {pending_earnings_count} payment{pending_earnings_count !== 1 ? 's' : ''} pending
                  </Text>
                  {eligible_earnings_amount > 0 && (
                    <Text fontSize="xs" color="green.400">
                      {formatCurrency(eligible_earnings_amount)} ready to claim
                    </Text>
                  )}
                </HStack>
              </Box>
            )}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default WalletStats;